import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardCheck,
  CheckCircle2,
  Circle,
  Clock,
  Award,
  Trophy,
  BarChart3,
  Repeat,
  Eye,
  Loader2,
  X,
  GraduationCap,
  AlertTriangle,
} from 'lucide-react';

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatElapsed = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));

  if (s < 60) return `${s}s`;

  const minutes = Math.floor(s / 60);
  const remaining = s % 60;

  return remaining === 0
    ? `${minutes}min`
    : `${minutes}:${String(remaining).padStart(2, '0')}`;
};

function ExamDetailDialog({
  review,
  open,
  onClose,
  onApprove,
  approving,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !review?.assignment_id) {
      setDetail(null);
      setError('');
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError('');
    setDetail(null);

    api.get(`/quiz-assignments/${review.assignment_id}`)
      .then((response) => {
        if (!cancelled) {
          setDetail(response.data);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
            'No se pudo cargar el detalle del examen'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, review?.assignment_id]);

  const activities = useMemo(() => {
    if (!detail) return [];

    const snapshots = detail?.quiz?.activity_snapshots || [];

    const list = snapshots
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const progressByPosition = new Map();

    (detail?.progress || []).forEach((progress) => {
      if (Number.isFinite(progress?.position)) {
        progressByPosition.set(Number(progress.position), progress);
      }
    });

    return list.map((entry, index) => {
      const position = Number(entry?.position ?? index);
      const activity = entry?.activity || entry || {};
      const progress = progressByPosition.get(position);

      return {
        position,
        title: activity?.title || `(Actividad ${position + 1})`,
        status: progress?.status === 'completed'
          ? 'completed'
          : 'pending',
        points_earned: progress?.points_earned ?? null,
        attempts_used: progress?.attempts_used ?? null,
        elapsed_seconds: progress?.elapsed_seconds ?? null,
      };
    });
  }, [detail]);

  if (!review) return null;

  const points = Number(
    detail?.points_obtained ??
    review?.points_obtained ??
    0
  );

  const maxPoints = Number(
    detail?.max_points ??
    review?.max_points ??
    0
  );

  /*
   * La aprobación académica usa raw_score.
   * final_score NO se utiliza para decidir la aprobación.
   */
  const rawScore =
    detail?.raw_score ??
    review?.raw_score ??
    null;

  const done = activities.filter(
    (activity) => activity.status === 'completed'
  ).length;

  const totalActivities = activities.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value && !approving) {
          onClose?.();
        }
      }}
    >
      <DialogContent
        data-testid="module-exam-review-detail"
        className="max-w-3xl w-[95vw] p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-start gap-3 font-display text-xl text-slate-900">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white grid place-items-center shrink-0">
              <GraduationCap className="w-5 h-5" />
            </div>

            <div className="min-w-0 flex-1">
              <div
                data-testid="module-exam-review-student"
                className="font-black text-lg sm:text-xl truncate"
              >
                {review.student_name || 'Alumno'}
              </div>

              <div
                data-testid="module-exam-review-title"
                className="text-xs sm:text-sm text-slate-500 font-normal truncate"
              >
                {review.quiz_title || 'Examen final de módulo'}
              </div>
            </div>
          </DialogTitle>

          <DialogDescription className="sr-only">
            Revisión académica de un examen final de módulo.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto px-4 sm:px-5 py-4">
          {loading && (
            <div
              data-testid="module-exam-review-loading"
              className="py-10 grid place-items-center text-slate-500"
            >
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <div className="text-sm">
                Cargando detalle del examen…
              </div>
            </div>
          )}

          {!loading && error && (
            <div
              data-testid="module-exam-review-error"
              className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-4 text-sm"
            >
              <div className="font-semibold mb-1">
                No pudimos cargar el examen
              </div>
              <div className="text-xs">{error}</div>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-4">

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Examen completado
                  </Badge>

                  <span
                    data-testid="module-exam-review-progress"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[12px] font-semibold text-slate-700 tabular-nums"
                  >
                    <BarChart3 className="w-3 h-3" />
                    {done} / {totalActivities} actividades
                  </span>

                  {maxPoints > 0 && (
                    <span
                      data-testid="module-exam-review-points"
                      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-1 text-[12px] font-semibold text-indigo-800 tabular-nums"
                    >
                      <Award className="w-3 h-3" />
                      {points} / {maxPoints} puntos
                    </span>
                  )}

                  {rawScore !== null && (
                    <span
                      data-testid="module-exam-review-raw-score"
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[12px] font-black text-emerald-800 tabular-nums"
                    >
                      <Trophy className="w-3 h-3" />
                      Nota real {Number(rawScore).toFixed(2)} / 10
                    </span>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px] text-slate-600">
                  <div className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    Inicio:
                    <span className="text-slate-800">
                      {formatDate(detail?.started_at ?? review?.started_at)}
                    </span>
                  </div>

                  <div className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
                    Finalización:
                    <span className="text-slate-800">
                      {formatDate(detail?.completed_at ?? review?.completed_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                  Actividades
                </div>

                <ol
                  data-testid="module-exam-review-activities"
                  className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white"
                >
                  {activities.length === 0 && (
                    <li className="px-3 py-6 text-center text-sm text-slate-400">
                      Este examen no tiene actividades.
                    </li>
                  )}

                  {activities.map((activity) => (
                    <li
                      key={activity.position}
                      data-testid={`module-exam-review-activity-${activity.position}`}
                      className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 text-sm"
                    >
                      <span className="text-xs text-slate-400 w-6 sm:w-8 shrink-0 font-mono tabular-nums">
                        #{activity.position + 1}
                      </span>

                      <span className="w-5 shrink-0 grid place-items-center">
                        {activity.status === 'completed'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <Circle className="w-4 h-4 text-slate-300" />}
                      </span>

                      <span className="flex-1 min-w-0 truncate text-slate-800">
                        {activity.title}
                      </span>

                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 text-[11px] text-slate-500 tabular-nums">
                        {activity.attempts_used != null && (
                          <span
                            className="inline-flex items-center gap-0.5"
                            title="Intentos utilizados"
                          >
                            <Repeat className="w-3 h-3" />
                            {activity.attempts_used}
                          </span>
                        )}

                        {activity.elapsed_seconds != null &&
                          activity.elapsed_seconds > 0 && (
                            <span
                              className="inline-flex items-center gap-0.5"
                              title="Tiempo empleado"
                            >
                              <Clock className="w-3 h-3" />
                              {formatElapsed(activity.elapsed_seconds)}
                            </span>
                          )}

                        <span className="font-semibold text-slate-700 min-w-[42px] text-right">
                          {activity.status === 'completed' &&
                          activity.points_earned != null
                            ? (
                              <>
                                <Award className="w-3 h-3 inline mr-0.5 text-indigo-500" />
                                {activity.points_earned}
                              </>
                            )
                            : <span className="text-slate-300">—</span>}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                    <ClipboardCheck className="w-4 h-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900">
                      Revisión académica
                    </div>

                    <div className="text-sm text-slate-600 mt-1">
                      La aprobación del módulo requiere una calificación real
                      de al menos 7 / 10.
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-lg bg-white border border-amber-200 px-3 py-2 text-sm font-bold text-slate-800">
                        Calificación real:
                        <span className="ml-1 text-amber-700">
                          {rawScore !== null
                            ? `${Number(rawScore).toFixed(2)} / 10`
                            : 'Sin calificación'}
                        </span>
                      </span>

                      <span className="inline-flex items-center rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-slate-600">
                        Mínimo:
                        <span className="ml-1 font-bold text-slate-800">
                          7 / 10
                        </span>
                      </span>
                    </div>

                    {rawScore !== null && Number(rawScore) < 7 && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-rose-700">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                          Esta calificación no alcanza el mínimo de aprobación.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex-col-reverse sm:flex-row">
          <Button
            data-testid="module-exam-review-close"
            variant="outline"
            onClick={() => onClose?.()}
            disabled={approving}
            className="w-full sm:w-auto"
          >
            <X className="w-4 h-4 mr-1" />
            Cerrar
          </Button>

          <Button
            data-testid="module-exam-review-approve"
            onClick={() => onApprove?.(review)}
            disabled={
              approving ||
              rawScore === null ||
              Number(rawScore) < 7
            }
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
          >
            {approving
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Aprobar módulo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ModuleExamReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = async () => {
    setLoading(true);

    try {
      const { data } = await api.get('/admin/module-exams/pending-review');
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        'Error cargando exámenes pendientes de revisión'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetail = (review) => {
    setSelectedReview(review);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    if (approving) return;

    setDetailOpen(false);
    setSelectedReview(null);
  };

  const approveReview = async (review) => {
    if (!review || approving) return;

    const rawScore = Number(review.raw_score);

    if (!Number.isFinite(rawScore) || rawScore < 7) {
      toast.error(
        'El examen no alcanza la calificación mínima de aprobación.'
      );
      return;
    }

    const confirmed = window.confirm(
      `¿Confirmas aprobar el Módulo ${review.module_slot} de ${review.student_name || 'este alumno'} con calificación ${rawScore.toFixed(2)}?`
    );

    if (!confirmed) return;

    setApproving(true);

    try {
      await api.post(
        `/admin/module-enrollments/${review.enrollment_id}/approve`
      );

      toast.success(
        `Módulo ${review.module_slot} aprobado para ${review.student_name || 'el alumno'}`
      );

      setDetailOpen(false);
      setSelectedReview(null);

      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        'No se pudo aprobar el módulo'
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        <PageHeader
          title="Revisión de exámenes"
          subtitle="Revisa los exámenes finales completados antes de aprobar académicamente el módulo."
        />

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 font-semibold">
              Pendientes de revisión
            </div>
            <div
              data-testid="module-exam-review-count"
              className="mt-1 text-2xl font-black text-slate-900"
            >
              {reviews.length}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 font-semibold">
              Etapa
            </div>
            <div className="mt-1 text-sm font-bold text-indigo-700">
              Revisión académica
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 font-semibold">
              Calificación mínima
            </div>
            <div className="mt-1 text-2xl font-black text-emerald-700">
              7 / 10
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div
              data-testid="module-exam-review-loading-list"
              className="py-14 grid place-items-center text-slate-500"
            >
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <div className="text-sm">
                Cargando exámenes pendientes…
              </div>
            </div>
          ) : reviews.length === 0 ? (
            <div
              data-testid="module-exam-review-empty"
              className="py-14 px-6 text-center"
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>

              <div className="mt-3 font-bold text-slate-900">
                No hay exámenes pendientes de revisión
              </div>

              <div className="mt-1 text-sm text-slate-500">
                Los exámenes finales completados aparecerán aquí hasta que
                un administrador los revise.
              </div>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">
                        Alumno
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">
                        Módulo
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">
                        Examen
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">
                        Puntos
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">
                        Nota real
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">
                        Acción
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {reviews.map((review) => (
                      <tr
                        key={review.enrollment_id}
                        data-testid={`module-exam-review-row-${review.enrollment_id}`}
                        className="hover:bg-slate-50/70"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">
                            {review.student_name || 'Alumno'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {review.student_email || '—'}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                            Módulo {review.module_slot}
                          </Badge>
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">
                            {review.quiz_title || 'Examen final'}
                          </div>
                          <div className="text-xs text-slate-500">
                            Completado: {formatDate(review.completed_at)}
                          </div>
                        </td>

                        <td className="px-4 py-3 text-right tabular-nums">
                          <span className="font-semibold text-slate-800">
                            {review.points_obtained ?? 0}
                          </span>
                          <span className="text-slate-400">
                            {' / '}
                            {review.max_points ?? 0}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right tabular-nums">
                          <span className="font-black text-emerald-700">
                            {review.raw_score != null
                              ? Number(review.raw_score).toFixed(2)
                              : '—'}
                          </span>
                          <span className="text-slate-400">
                            {' / 10'}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right">
                          <Button
                            data-testid={`module-exam-review-open-${review.enrollment_id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => openDetail(review)}
                            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver examen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-slate-100">
                {reviews.map((review) => (
                  <div
                    key={review.enrollment_id}
                    data-testid={`module-exam-review-card-${review.enrollment_id}`}
                    className="p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">
                          {review.student_name || 'Alumno'}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {review.student_email || '—'}
                        </div>
                      </div>

                      <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 shrink-0">
                        Módulo {review.module_slot}
                      </Badge>
                    </div>

                    <div className="mt-3 text-sm text-slate-700">
                      {review.quiz_title || 'Examen final'}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1">
                        {review.points_obtained ?? 0} / {review.max_points ?? 0} puntos
                      </span>

                      <span className="rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 font-bold text-emerald-700">
                        Nota {review.raw_score != null
                          ? Number(review.raw_score).toFixed(2)
                          : '—'} / 10
                      </span>
                    </div>

                    <Button
                      data-testid={`module-exam-review-open-mobile-${review.enrollment_id}`}
                      variant="outline"
                      onClick={() => openDetail(review)}
                      className="w-full mt-3 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver examen
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <ExamDetailDialog
        review={selectedReview}
        open={detailOpen}
        onClose={closeDetail}
        onApprove={approveReview}
        approving={approving}
      />
    </div>
  );
}
